import { useState, useEffect, useCallback, useRef } from "react"
import API from "../utils/API"

// Cache for feature flag states to avoid redundant API calls
const featureFlagCache = new Map()
const pendingRequests = new Map()

export default function useFeatureFlag(featureCode) {
  const [isActive, setIsActive] = useState(() => {
    // Check cache for initial value
    const cached = featureFlagCache.get(featureCode)

    return cached !== undefined ? cached.isActive : false
  })
  const [feature, setFeature] = useState(() => {
    // Check cache for initial value
    const cached = featureFlagCache.get(featureCode)

    return cached !== undefined ? cached.feature : null
  })
  const [isLoading, setIsLoading] = useState(() => {
    // Only loading if not cached
    return !featureFlagCache.has(featureCode)
  })
  const [error, setError] = useState(null)
  const isMounted = useRef(true)

  const checkFeature = useCallback(() => {
    // If already cached, use cached value
    if (featureFlagCache.has(featureCode)) {
      const cached = featureFlagCache.get(featureCode)
      setIsActive(cached.isActive)
      setFeature(cached.feature)
      setIsLoading(false)

      return
    }

    // If there's already a pending request for this feature, wait for it
    if (pendingRequests.has(featureCode)) {
      pendingRequests.get(featureCode).then((result) => {
        if (isMounted.current) {
          setIsActive(result.isActive)
          setFeature(result.feature)
          setIsLoading(false)
        }
      })

      return
    }

    const requestPromise = new Promise((resolve) => {
      API.GET(
        `/feature/${featureCode}`,
        (response) => {
          const featureData = response.data || null
          const active = featureData?.isActive || false
          const cacheValue = { isActive: active, feature: featureData }

          featureFlagCache.set(featureCode, cacheValue)

          if (isMounted.current) {
            setIsActive(active)
            setFeature(featureData)
            setIsLoading(false)
            setError(null)
          }

          resolve(cacheValue)
        },
        () => {
          // On error, assume feature is not active
          const cacheValue = { isActive: false, feature: null }

          featureFlagCache.set(featureCode, cacheValue)

          if (isMounted.current) {
            setIsActive(false)
            setFeature(null)
            setIsLoading(false)
            setError("Failed to fetch feature")
          }

          resolve(cacheValue)
        }
      )
    })

    pendingRequests.set(featureCode, requestPromise)

    requestPromise.finally(() => {
      pendingRequests.delete(featureCode)
    })
  }, [featureCode])

  useEffect(() => {
    isMounted.current = true

    checkFeature()

    return () => {
      isMounted.current = false
    }
  }, [checkFeature])

  return { isActive, feature, isLoading, error }
}

export const FEATURE_CODES = {
  AI_TARGETING: "AI_TARGETING",
}

export function doesTeamHaveAccessToFeature(team, featureCode) {
  return team?.features?.some((feature) => feature.code === featureCode) ?? false
}
